import React, { useCallback, useMemo, useState } from 'react';
import cn from 'clsx';
import { createElementKey } from 'utils';
import {
  Section,
  SectionTitle,
  SelectionGroup,
  SelectionGroupOption,
  SelectionGroupValue,
  Typography,
} from 'common/components';
import { analyticsUtils } from 'utils/analytics-utils';
import {
  adplatformTrainingReelsSectionGrid,
  adplatformTrainingReelsSectionInstruction,
  adplatformTrainingReelsSectionInstructionLast,
  adplatformTrainingReelsSectionTabs,
  adplatformTrainingReelsSectionText,
  adplatformTrainingReelsSectionTextContent,
  adplatformTrainingReelsSectionTitle,
} from './adplatform-training-reels-section-styles';
import {
  ADPLATFORM_TRAINING_INSTRUCTION_DATA,
  ADPLATFORM_TRAINING_MEDIA_DATA,
  ADPLATFORM_TRAINING_REELS_LIST,
  ADPLATFORM_TRAINING_REELS_TABS_LIST,
  AdplatformTrainingReelsTabName,
  AdplatformTrainingReelStructure,
} from './adplatform-training-reels-section-constants';
import { ADPLATFORM_TRAINING_ELEMENT_BLOCK } from '../../adplatform-training-constants';
import { ReelItem, ReelModal } from './components';

const getActiveOptionText = (activeReelsTypeTab: AdplatformTrainingReelsTabName) => ADPLATFORM_TRAINING_REELS_TABS_LIST.find(
  (option: SelectionGroupOption) => option.value === activeReelsTypeTab
);

export const AdplatformTrainingReelsSection: React.FC = () => {
  const [activeReelsTypeTab, setactiveReelsTypeTab] = useState(AdplatformTrainingReelsTabName.AllReels);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [activeReelIndex, setActiveReelIndex] = useState(0);

  const reelsList = useMemo(
    () =>
      activeReelsTypeTab === AdplatformTrainingReelsTabName.AllReels
        ? ADPLATFORM_TRAINING_REELS_LIST
        : ADPLATFORM_TRAINING_REELS_LIST.filter(list => list.type === activeReelsTypeTab),
    [activeReelsTypeTab]
  );

  const handleReelsTypeTabChange = (_: React.MouseEvent, value: SelectionGroupValue) => {
    setactiveReelsTypeTab(value as AdplatformTrainingReelsTabName);
    const activeOption = getActiveOptionText(value as AdplatformTrainingReelsTabName);

    if (activeOption) {
      analyticsUtils.clickButtonEvent(
        ADPLATFORM_TRAINING_ELEMENT_BLOCK,
        activeOption.text,
        'Лемана ПРО: обучение по рекламной платформе'
      );
    }
  };

  const handleReelsItemClick = (item: AdplatformTrainingReelStructure) => () => {
    const activeOption = getActiveOptionText(activeReelsTypeTab);

    analyticsUtils.clickButtonEvent(
      ADPLATFORM_TRAINING_ELEMENT_BLOCK,
      item.title,
      `Лемана ПРО: обучение по рекламной платформе / ${activeOption?.text ?? ''}`
    );
  };

  const handleReelsReelClick = (item: AdplatformTrainingReelStructure, index: number) => () => {
    handleReelsItemClick(item)();
    setActiveReelIndex(index);
    setIsModalOpen(true);
  };

  const handleReelModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <Section>
      <div className={adplatformTrainingReelsSectionTextContent}>
        <SectionTitle as="h1" className={adplatformTrainingReelsSectionTitle}>
          Лемана ПРО: обучение по&nbsp;рекламной платформе
        </SectionTitle>
        <Typography className={adplatformTrainingReelsSectionText} variant="l" size="body_long">
          Расскажем, как запускать рекламу на&nbsp;сайте и&nbsp;в&nbsp;приложении
        </Typography>
      </div>
      <SelectionGroup
        className={adplatformTrainingReelsSectionTabs}
        optionList={ADPLATFORM_TRAINING_REELS_TABS_LIST}
        onChange={handleReelsTypeTabChange}
        active={activeReelsTypeTab}
      />
      <ul className={adplatformTrainingReelsSectionGrid}>
        <ReelItem
          liClassName={cn(adplatformTrainingReelsSectionInstruction, {
            [adplatformTrainingReelsSectionInstructionLast]:
              activeReelsTypeTab !== AdplatformTrainingReelsTabName.AllReels,
          })}
          reel={ADPLATFORM_TRAINING_MEDIA_DATA}
          onClick={handleReelsItemClick(ADPLATFORM_TRAINING_MEDIA_DATA)}
        />
        <ReelItem
          liClassName={cn(adplatformTrainingReelsSectionInstruction, {
            [adplatformTrainingReelsSectionInstructionLast]:
              activeReelsTypeTab !== AdplatformTrainingReelsTabName.AllReels,
          })}
          reel={ADPLATFORM_TRAINING_INSTRUCTION_DATA}
          onClick={handleReelsItemClick(ADPLATFORM_TRAINING_INSTRUCTION_DATA)}
        />
        {reelsList.map((reel, index) => (
          <ReelItem
            key={createElementKey('reel', index)}
            reel={reel}
            onClick={handleReelsReelClick(reel, index)}
          />
        ))}
      </ul>
      <ReelModal onClose={handleReelModalClose} show={isModalOpen} activeReel={reelsList[activeReelIndex]} />
    </Section>
  );
};
